import { handleUserMessageCopy } from '@common/Copilot/CopilotAgentChat/agent-v4/conversationCopyUtils';
import SubActionButtons from '@common/Copilot/CopilotAgentChat/agent-v4/SubActionButtons';
import { defaultStepTitle, toToolName } from '@prophecy/shared-core/agentChat/format';
import { AgentErrorBody } from '@prophecy/shared-ui/components/agentChat/AgentErrorBody';
import { AgentMarkdown } from '@prophecy/shared-ui/components/agentChat/AgentMarkdown';
import { AgentReasoningBody } from '@prophecy/shared-ui/components/agentChat/AgentReasoningBody';
import { AgentStepList } from '@prophecy/shared-ui/components/agentChat/AgentStepList';
import { AgentToolUseBody } from '@prophecy/shared-ui/components/agentChat/AgentToolUseBody';
import { theme } from '@prophecy/theme';
import { Button } from '@prophecy/ui-v3/Button';
import { Stack } from '@prophecy/ui-v3/Layout';
import { Fragment, useMemo, useState } from 'react';
import styled from 'styled-components';

import { buildTurns, HOME_CHAT_ELEMENT_TYPES } from './chatTranscript';
import { PromptAttachments } from './ComposerAttachments';
import { HOME_CHAT_TEST_IDS } from './constants';
import { renderHomeMention } from './homeChatMentions';
import { McpWidgetFrame } from './McpWidgetFrame';
import { UserInputRequestCard } from './PendingUserInput';
import { resourceUriOf } from './widgetHost';

import type { HomeChatElement, HomeChatMessages, HomeChatTurn } from './chatTranscript';
import type { HomeChatWidget, WidgetHostApi } from './McpWidgetFrame';
import type { CopilotUserMessageTypeV4 } from '@common/Copilot/types';
import type {
  AgentPermissionOutcome,
  AgentStep,
  AgentStepKind,
  AgentTurn
} from '@prophecy/shared-core/agentChat/types';

const UserPrompt = styled(Stack)`
  align-self: flex-end;
  align-items: flex-end;
  max-width: 80%;
`;

const UserBubble = styled(Stack)`
  /* A table name carries no space to break at, so its min-content width would burst the column. */
  word-break: break-word;
  padding: ${theme.spaces.x8} ${theme.spaces.x12};
  border: 1px solid ${theme.colors.brand200};
  border-radius: ${theme.radius.x8} 0 ${theme.radius.x8} ${theme.radius.x8};
  background: ${theme.colors.brand50};
`;

// Same type as the ai-thread widget's answer (`AnswerBlock`), so a reply reads alike in both.
const Answer = styled(Stack)`
  && code:not(.entity-chip) {
    font-size: ${theme.text.sm.fontSize};
  }

  && strong {
    font-weight: ${theme.fontWeight.medium};
  }
`;

const LoadOlderChip = styled(Button)`
  border-radius: ${theme.radius.full};
`;

const TurnContainer = styled(Stack)`
  width: 100%;
`;

function text(element: HomeChatElement, key: string): string {
  const value = element.properties?.[key];
  return typeof value === 'string' ? value : '';
}

function toKind(element: HomeChatElement): AgentStepKind {
  switch (element.type) {
    case HOME_CHAT_ELEMENT_TYPES.ToolUse:
      return 'tool_use';
    case HOME_CHAT_ELEMENT_TYPES.Error:
      return 'error';
    case HOME_CHAT_ELEMENT_TYPES.UserInputRequest:
      return element.properties?.kind === 'permission_request' ? 'permission_request' : 'clarifying_questions';
    default:
      return 'reasoning';
  }
}

function toPermission(element: HomeChatElement): AgentPermissionOutcome | undefined {
  if (element.type !== HOME_CHAT_ELEMENT_TYPES.UserInputRequest) return undefined;
  return { answered: element.properties?.answered === true, cancelled: element.properties?.cancelled === true };
}

function toStep(element: HomeChatElement): AgentStep<HomeChatElement> {
  const kind = toKind(element);
  const permission = toPermission(element);
  const title =
    (kind === 'tool_use' ? text(element, 'toolTitle') || text(element, 'title') : text(element, 'title')) ||
    defaultStepTitle(kind, permission);
  const { startTime, endTime } = element.metainfo ?? {};
  return {
    id: element.id,
    kind,
    title,
    toolName: kind === 'tool_use' ? toToolName(title) : undefined,
    durationMs: startTime != null && endTime != null ? endTime - startTime : undefined,
    permission,
    defaultCollapsed: kind !== 'error',
    source: element
  };
}

function toAgentTurn(id: string, steps: HomeChatElement[], isRunning: boolean): AgentTurn<HomeChatElement> {
  const first = steps[0]?.metainfo?.startTime;
  const lastStep = steps[steps.length - 1];
  const last = lastStep?.metainfo?.endTime ?? lastStep?.metainfo?.lastUpdateTime;
  return {
    id,
    steps: steps.map(toStep),
    thinkingTimeMs: !isRunning && first != null && last != null ? last - first : undefined
  };
}

function widgetOf(element: HomeChatElement): HomeChatWidget | undefined {
  const widget = element.properties?.widget as HomeChatWidget | undefined;
  return element.type === HOME_CHAT_ELEMENT_TYPES.ToolUse && widget?.structuredContent && resourceUriOf(widget)
    ? widget
    : undefined;
}

// An answered card is a read-only summary; it never sends again.
const ANSWERED = async () => ({ success: true });

function renderStepBody(step: AgentStep<HomeChatElement>, animate: boolean) {
  const element = step.source;
  switch (element.type) {
    case HOME_CHAT_ELEMENT_TYPES.ToolUse:
      return <AgentToolUseBody request={element.properties?.request} response={element.properties?.response} />;
    case HOME_CHAT_ELEMENT_TYPES.Error:
      return <AgentErrorBody description={text(element, 'description')} />;
    case HOME_CHAT_ELEMENT_TYPES.AgentThinking:
    case HOME_CHAT_ELEMENT_TYPES.AgentMessage:
      return <AgentReasoningBody text={text(element, 'content')} animate={animate} />;
    case HOME_CHAT_ELEMENT_TYPES.UserInputRequest:
      return <UserInputRequestCard element={element} sendAnswer={ANSWERED} />;
    default:
      return null;
  }
}

type WidgetProps = {
  widgetHost: WidgetHostApi;
  onFollowUp: (text: string) => Promise<unknown>;
};

type StepSegment = { id: string; steps: HomeChatElement[]; widget?: { id: string; widget: HomeChatWidget } };

function turnIdOf(widget: HomeChatWidget): string | undefined {
  const turnId = (widget.structuredContent as { data?: { turnId?: unknown } } | null)?.data?.turnId;
  return typeof turnId === 'string' ? turnId : undefined;
}

/**
 * Steps in call order, cut after each tool call that drew a widget, so its card follows it. Later
 * calls that wait on the card's turn (prophecy_ai_wait) stay in its group, which is timed through them.
 */
function segmentsOf(steps: HomeChatElement[]): StepSegment[] {
  const segments: StepSegment[] = [];
  let pending: HomeChatElement[] = [];
  let cardTurnId: string | undefined;
  for (const step of steps) {
    pending.push(step);
    const widget = widgetOf(step);
    if (widget) {
      segments.push({ id: pending[0].id, steps: pending, widget: { id: step.id, widget } });
      pending = [];
      cardTurnId = turnIdOf(widget);
    } else if (cardTurnId && text(step, 'request').includes(`\`${cardTurnId}\``)) {
      const card = segments[segments.length - 1];
      card.steps = [...card.steps, ...pending];
      pending = [];
    }
  }
  if (pending.length) segments.push({ id: pending[0].id, steps: pending });
  return segments;
}

function StepGroup({ id, steps, isRunning }: { id: string; steps: HomeChatElement[]; isRunning: boolean }) {
  const agentTurn = useMemo(() => toAgentTurn(id, steps, isRunning), [id, steps, isRunning]);
  // A finished group folds its steps away, unless it failed: the error stays in view.
  const foldWhenDone = !steps.some((step) => step.type === HOME_CHAT_ELEMENT_TYPES.Error);
  const [collapsed, setCollapsed] = useState(!isRunning && foldWhenDone);
  const [wasRunning, setWasRunning] = useState(isRunning);
  if (wasRunning !== isRunning) {
    setWasRunning(isRunning);
    setCollapsed(!isRunning && foldWhenDone);
  }
  return (
    <AgentStepList
      turn={agentTurn}
      animate={isRunning}
      turnCollapsed={collapsed}
      onTurnCollapsedChange={setCollapsed}
      renderBody={(step) => renderStepBody(step, isRunning)}
    />
  );
}

function TurnView({
  turn,
  isRunning,
  widgetHost,
  onFollowUp
}: { turn: HomeChatTurn; isRunning: boolean } & WidgetProps) {
  const segments = useMemo(() => segmentsOf(turn.steps), [turn.steps]);
  const prompt = turn.user ? text(turn.user, 'text') : '';
  return (
    <TurnContainer gap={theme.spaces.x20}>
      {turn.user ? (
        <UserPrompt gap={theme.spaces.x4}>
          <UserBubble gap={theme.spaces.x8} data-test-id={HOME_CHAT_TEST_IDS.userMessage}>
            {turn.user.properties?.context ? <PromptAttachments context={turn.user.properties.context} /> : null}
            <AgentMarkdown message={prompt} disableFixedHeight renderMention={renderHomeMention} />
          </UserBubble>
          <SubActionButtons onCopy={() => void handleUserMessageCopy(turn.user as CopilotUserMessageTypeV4)} />
        </UserPrompt>
      ) : null}
      {segments.map((segment, index) => (
        <Fragment key={segment.id}>
          {/* Only the trailing group can still be streaming; one ending at its card is finished. */}
          <StepGroup
            id={segment.id}
            steps={segment.steps}
            isRunning={isRunning && index === segments.length - 1 && segment.steps.at(-1)?.id !== segment.widget?.id}
          />
          {segment.widget ? (
            <McpWidgetFrame widget={segment.widget.widget} host={widgetHost} onFollowUp={onFollowUp} />
          ) : null}
        </Fragment>
      ))}
      {turn.answer ? (
        <Answer data-test-id={HOME_CHAT_TEST_IDS.answer}>
          <AgentMarkdown message={text(turn.answer, 'content')} disableFixedHeight />
        </Answer>
      ) : null}
    </TurnContainer>
  );
}

export function Transcript({
  messages,
  isWorking,
  hasOlder,
  isLoadingOlder,
  onLoadOlder,
  widgetHost,
  onFollowUp
}: {
  messages: HomeChatMessages;
  isWorking: boolean;
  hasOlder: boolean;
  isLoadingOlder: boolean;
  onLoadOlder: () => void;
} & WidgetProps) {
  const turns = useMemo(() => buildTurns(messages), [messages]);

  return (
    <Stack gap={theme.spaces.x24} width='100%'>
      {hasOlder ? (
        <Stack alignY='center'>
          <LoadOlderChip
            variant='secondaryGray'
            size='xs'
            loading={isLoadingOlder}
            onClick={onLoadOlder}
            data-test-id={HOME_CHAT_TEST_IDS.loadOlder}>
            Load older messages
          </LoadOlderChip>
        </Stack>
      ) : null}
      {turns.map((turn, index) => (
        <TurnView
          key={turn.id}
          turn={turn}
          isRunning={isWorking && index === turns.length - 1}
          widgetHost={widgetHost}
          onFollowUp={onFollowUp}
        />
      ))}
    </Stack>
  );
}
